import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { mainStore } from '~/stores';
import { COOKIE_CONSENT_KEY, getCookieValue, setCookie } from '~/utils/cookies';

import styles from './CookieBanner.module.scss';

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const CookieBanner = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const cookiePolicyLink = useMemo(() => {
    return (
      company.config.links.legals?.cookiePolicy?.link ||
      company.config.links.cookie ||
      '/legals#cookies'
    );
  }, []);

  useEffect(() => {
    if (import.meta.env.SSR) {
      return;
    }

    if (!company.config.showCookieBanner || mainStore.isRN) {
      return;
    }

    const consentValue = getCookieValue(COOKIE_CONSENT_KEY);
    if (!consentValue) {
      setIsVisible(true);
    }
  }, []);

  const setConsent = (value: 'accepted' | 'declined' | 'dismissed') => {
    setCookie(COOKIE_CONSENT_KEY, value, { 'max-age': COOKIE_MAX_AGE });
    setIsVisible(false);
  };

  if (!company.config.showCookieBanner || !isVisible) {
    return null;
  }

  return (
    <>
      <div className={styles.overlay} />
      <div className={styles.banner} role="dialog" aria-live="polite">
        <button
          type="button"
          className={styles.closeButton}
          onClick={() => setConsent('dismissed')}
          aria-label={t('close')}
        >
          <Icon type="close" />
        </button>
        <div className={styles.content}>
          <div className={styles.title}>{t('phrases:cookieBannerTitle')}</div>
          <div className={styles.description}>
            <Trans
              i18nKey="phrases:cookieBannerDescription"
              components={[
                <Link
                  to={cookiePolicyLink}
                  className="link"
                  key="cookie-link"
                />,
              ]}
            />
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => setConsent('accepted')}
          >
            {t('phrases:cookieBannerAccept')}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setConsent('declined')}
          >
            {t('phrases:cookieBannerDecline')}
          </button>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
